import { useEffect } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import stationCoords from "../services/stationCoords";

// Fix leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;

function createSvgIcon(color) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="42" viewBox="0 0 28 42">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 28 14 28s14-17.5 14-28C28 6.27 21.73 0 14 0z" fill="${color}"/>
        <circle cx="14" cy="14" r="6" fill="white"/>
    </svg>`;
    return new L.Icon({
        iconUrl: `data:image/svg+xml;base64,${btoa(svg)}`,
        iconSize: [28, 42],
        iconAnchor: [14, 42],
        popupAnchor: [0, -36],
    });
}

const departureIcon = createSvgIcon("#1a1a2e");
const arrivalIcon = createSvgIcon("#FF5630");

function FitBounds({ depCoords, arrCoords }) {
    const map = useMap();

    useEffect(() => {
        if (depCoords && arrCoords) {
            const bounds = L.latLngBounds([depCoords, arrCoords]);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
        }
    }, [map, depCoords, arrCoords]);

    return null;
}

export default function RouteMap({ departureStation, arrivalStation, depTime, arrTime }) {
    const depCoords = stationCoords(departureStation);
    const arrCoords = stationCoords(arrivalStation);

    const depName = departureStation?.name || departureStation?.stationName || "";
    const arrName = arrivalStation?.name || arrivalStation?.stationName || "";

    if (!depCoords || !arrCoords) {
        return null;
    }

    const center = [
        (depCoords[0] + arrCoords[0]) / 2,
        (depCoords[1] + arrCoords[1]) / 2,
    ];

    return (
        <Box
            sx={{
                height: 350,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "grey.200",
                mt: 1,
            }}
        >
            <MapContainer
                center={center}
                zoom={7}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <FitBounds depCoords={depCoords} arrCoords={arrCoords} />

                <Marker position={depCoords} icon={departureIcon}>
                    <Popup>
                        <Stack spacing={0.5} sx={{ minWidth: 120 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {depName}
                            </Typography>
                            {depTime && (
                                <Typography variant="caption" color="text.secondary">
                                    Polazak: {depTime}
                                </Typography>
                            )}
                        </Stack>
                    </Popup>
                </Marker>

                <Marker position={arrCoords} icon={arrivalIcon}>
                    <Popup>
                        <Stack spacing={0.5} sx={{ minWidth: 120 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {arrName}
                            </Typography>
                            {arrTime && (
                                <Typography variant="caption" color="text.secondary">
                                    Dolazak: {arrTime}
                                </Typography>
                            )}
                        </Stack>
                    </Popup>
                </Marker>

                <Polyline
                    positions={[depCoords, arrCoords]}
                    pathOptions={{
                        color: "#3366FF",
                        weight: 3,
                        opacity: 0.7,
                        dashArray: "10, 6",
                    }}
                />
            </MapContainer>
        </Box>
    );
}
